import React, { useEffect, useRef, useState } from "react";
import { ShoppingBag, Check } from "lucide-react";
import { useCart } from "../../context/CartContext";
import toast from "react-hot-toast";
import { formatPrice } from "../../utils/format";
import { optimizeImageUrl } from "../../utils/images";

const FALLBACK = optimizeImageUrl("https://images.unsplash.com/photo-1590736969955-71cc94901144?w=200&q=80", { width: 500 });

const STOCK_STATUS_META = {
  in_stock: {
    label: "In Stock",
    pillClass: "bg-emerald-50 text-emerald-700 border border-emerald-100",
  },
  out_of_stock: {
    label: "Out of Stock",
    pillClass: "bg-amber-50 text-amber-700 border border-amber-100",
  },
  sold_out: {
    label: "Sold Out",
    pillClass: "bg-rose-50 text-rose-700 border border-rose-100",
  },
};

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);
  const resetTimerRef = useRef(null);
  const stockStatus = product.stock_status || "in_stock";
  const stockMeta = STOCK_STATUS_META[stockStatus] || STOCK_STATUS_META.in_stock;
  const canAddToCart = stockStatus === "in_stock";

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  const handleAdd = () => {
    if (!canAddToCart) {
      toast.error(`${product.name} is currently unavailable.`);
      return;
    }

    addItem(product);
    setAdded(true);
    toast.success(`${product.name} added to cart!`);
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
    }
    resetTimerRef.current = setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="group relative bg-white border border-gray-100 hover:border-gray-200 hover:shadow-xl transition-all duration-500 rounded-sm overflow-hidden">
      <div className="relative overflow-hidden bg-brand-cream aspect-[3/4]">
        <img
          src={optimizeImageUrl(product.image_url || FALLBACK, { width: 700 })}
          alt={product.name}
          className={`w-full h-full object-cover transition-transform duration-700 ${canAddToCart ? "group-hover:scale-105" : "opacity-70"}`}
          onError={(e) => { e.currentTarget.src = FALLBACK; }}
          loading="eager"
          fetchPriority="high"
          sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
        />
        {product.category && (
          <span className="absolute top-3 left-3 px-2.5 py-1 bg-white/90 backdrop-blur-sm text-brand-charcoal font-body text-[10px] uppercase tracking-widest rounded-sm">
            {product.category}
          </span>
        )}
        <div className="absolute top-3 right-3 flex flex-col items-end gap-2">
          {product.featured && (
            <span className="px-2.5 py-1 bg-brand-red text-white font-body text-[10px] uppercase tracking-widest rounded-sm">
              Featured
            </span>
          )}
          <span className={`px-2.5 py-1 font-body text-[10px] uppercase tracking-widest rounded-sm ${stockMeta.pillClass}`}>
            {stockMeta.label}
          </span>
        </div>
      </div>

      <div className="p-3 sm:p-4">
        <h3 className="font-display text-base sm:text-lg text-brand-charcoal leading-tight mb-1 min-h-[2.5rem] sm:min-h-[3.25rem] line-clamp-2">
          {product.name}
        </h3>
        <p className="font-body text-[10px] uppercase tracking-[0.25em] text-brand-red/80 mb-2">Fast Delivery</p>
        {product.description && (
          <p className="font-body text-[11px] sm:text-xs text-gray-400 line-clamp-2 mb-3 min-h-[2rem] sm:min-h-[2.5rem]">
            {product.description}
          </p>
        )}
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <div className="flex flex-col">
            <span className="font-body font-semibold text-sm sm:text-base text-brand-charcoal">
              {formatPrice(product.price)}
            </span>
            {!canAddToCart && (
              <span className="font-body text-[11px] text-gray-400 mt-1">
                This item is currently unavailable for purchase.
              </span>
            )}
          </div>
          <button
            onClick={handleAdd}
            disabled={!canAddToCart}
            className={`w-full sm:w-auto justify-center flex items-center gap-1.5 px-3 py-2 text-[11px] sm:text-xs font-body font-medium tracking-wide rounded-sm transition-all duration-300 shrink-0 ${
              !canAddToCart
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : added
                  ? "bg-green-600 text-white"
                  : "bg-brand-red text-white hover:bg-red-700"
            }`}
          >
            {!canAddToCart ? "Unavailable" : added ? <><Check size={12} />Added</> : <><ShoppingBag size={12} />Add to Cart</>}
          </button>
        </div>
        <p className="font-body text-[11px] text-gray-400 mt-3">Authentic product</p>
      </div>
    </div>
  );
}
